import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AppLayout } from '../components/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { 
  Megaphone, Clock, CheckCircle2, XCircle, 
  Loader2, Eye, Pencil, Send, Settings,
  ChevronDown, ChevronUp, Save, ArrowLeft,
  Plus, Trash2, Mail, Users, FileText,
  AlertCircle, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { PageHeader, Segmented } from '../components/ds';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  brouillon: { label: 'Brouillon', color: 'bg-surface-2 text-[#2E2D2A]', icon: Pencil },
  en_attente: { label: 'En attente', color: 'bg-orange-tint text-orange-tint-text', icon: Clock },
  valide: { label: 'Validé / Envoyé', color: 'bg-graphite-900 text-white', icon: CheckCircle2 },
  refuse: { label: 'Refusé', color: 'bg-surface-2 text-[#2E2D2A]', icon: XCircle },
};

const safeDate = (d, fmt = 'dd/MM/yyyy HH:mm') => {
  try { return format(parseISO(d), fmt, { locale: fr }); } catch { return '-'; }
};

function AnnouncementPreview({ content }) {
  if (!content) return null;
  const rm = content.renseignements_magasin || {};
  const dm = content.data_magasin || {};
  const capex = content.investissement_capex || {};
  const rh = content.personnel_rh || {};
  const sc = content.situation_corner || {};
  const ct = content.contrat || {};
  const sf = content.salesforce || {};

  const fmtNum = (v) => {
    try {
      const n = parseFloat(v);
      return isNaN(n) ? v : n.toLocaleString('fr-FR');
    } catch { return v; }
  };

  const Row = ({ label, val, suffix = '' }) => {
    if (!val && val !== 0) return null;
    return (
      <div className="flex justify-between py-1 border-b border-slate-100 dark:border-slate-700 last:border-0">
        <span className="text-xs text-slate-500">{label}</span>
        <span className="text-xs font-medium text-slate-800 dark:text-slate-200">{typeof val === 'number' ? fmtNum(val) : val}{suffix}</span>
      </div>
    );
  };

  const Section = ({ title, children }) => {
    const hasContent = React.Children.toArray(children).some(c => c !== null);
    if (!hasContent) return null;
    return (
      <div className="mb-3">
        <h4 className="text-xs font-bold text-emerald-600 uppercase tracking-wide mb-1 border-b border-emerald-200 pb-1">{title}</h4>
        {children}
      </div>
    );
  };

  const commissionRate = (() => {
    const r = ct.taux_commission;
    if (typeof r === 'number' && r < 1) return Math.round(r * 100);
    return r;
  })();

  return (
    <div className="bg-white dark:bg-slate-900 rounded-lg border p-4 space-y-2 max-h-96 overflow-y-auto text-sm" data-testid="announcement-preview">
      <div className="bg-emerald-50 dark:bg-emerald-900/30 rounded-lg p-3 text-center mb-3">
        <p className="text-lg font-bold text-emerald-700 dark:text-emerald-400">
          {content.enseigne} – {content.ville}
        </p>
      </div>

      <Section title="Renseignements magasin">
        <Row label="Enseigne" val={rm.enseigne} />
        <Row label="Adresse" val={rm.adresse} />
        <Row label="Ville" val={`${rm.code_postal || ''} ${rm.ville || ''}`} />
        <Row label="Surface" val={rm.surface_m2} suffix=" m²" />
        <Row label="Concept" val={rm.concept} />
      </Section>

      <Section title="Data magasin">
        <Row label="Zone RM" val={dm.zone_rm} />
        <Row label="Revenu médian" val={dm.revenu_median} suffix=" €" />
        <Row label="Zone voiture" val={dm.zone_chalandise_voiture} suffix=" min" />
        <Row label="Zone pied" val={dm.zone_chalandise_pied} suffix=" min" />
      </Section>

      <Section title="Investissement CAPEX">
        <Row label={`Corner (${capex.corner_investisseur})`} val={capex.corner_montant} suffix=" €" />
        <Row label={`Vitrine (${capex.vitrine_investisseur})`} val={capex.vitrine_montant} suffix=" €" />
        <Row label={`Inox (${capex.inox_investisseur})`} val={capex.inox_montant} suffix=" €" />
        <Row label="Total CAPEX" val={capex.total_capex} suffix=" €" />
        <Row label="CAPEX CCD" val={capex.capex_ccd} suffix=" €" />
      </Section>

      {Object.values(rh).some(v => v > 0) && (
        <Section title="Personnel RH">
          <Row label="Directeur" val={rh.directeur > 0 ? rh.directeur : null} />
          <Row label="Responsable" val={rh.responsable > 0 ? rh.responsable : null} />
          <Row label="Cuisinier" val={rh.cuisinier > 0 ? rh.cuisinier : null} />
          <Row label="Polyvalent" val={rh.polyvalent > 0 ? rh.polyvalent : null} />
          <Row label="Plongeur" val={rh.plongeur > 0 ? rh.plongeur : null} />
        </Section>
      )}

      <Section title="Contrat">
        <Row label="Commission" val={commissionRate} suffix="%" />
        <Row label="Durée" val={ct.duree_mois} suffix=" mois" />
        <Row label="CA prévisionnel HT" val={ct.ca_previsionnel_ht} suffix=" €/an" />
        <Row label="Raison sociale" val={ct.raison_sociale} />
      </Section>

      {sf.informations && (
        <Section title="Salesforce">
          <p className="text-xs text-slate-600 whitespace-pre-wrap">{sf.informations}</p>
        </Section>
      )}

      {content.message_final && (
        <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-3 mt-2 border-l-4 border-emerald-500">
          <p className="text-xs text-emerald-800 dark:text-emerald-300 whitespace-pre-line">{content.message_final}</p>
        </div>
      )}
    </div>
  );
}

export default function AnnouncementsPage() {
  const { user, token } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAnn, setSelectedAnn] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewEditMode, setPreviewEditMode] = useState(false);
  const [htmlEditMode, setHtmlEditMode] = useState(false);
  const [editedHtml, setEditedHtml] = useState('');
  const [savingHtml, setSavingHtml] = useState(false);
  const [isCustomHtml, setIsCustomHtml] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editContent, setEditContent] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectComment, setRejectComment] = useState('');
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [distributionEmail, setDistributionEmail] = useState('');
  const [emailTemplate, setEmailTemplate] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [filter, setFilter] = useState('all');

  // Recipients management
  const [recipients, setRecipients] = useState([]);
  const [newRecipientEmail, setNewRecipientEmail] = useState('');
  const [newRecipientLabel, setNewRecipientLabel] = useState('');
  const [loadingRecipients, setLoadingRecipients] = useState(false);

  // Approve with recipient selection
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [extraRecipient, setExtraRecipient] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [annToDelete, setAnnToDelete] = useState(null);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  // Default recipients toujours pré-cochés dans le SAS de validation Annonce ALL
  const DEFAULT_PRECHECKED_EMAILS = ['c.perez@wasabicorner.fr', 'all@wasabicorner.fr'];

  // Liste affichée dans la modale : fusion entre les destinataires DB actifs + les pré-cochés par défaut
  // garantit que les 2 emails par défaut sont toujours visibles + cochables, même s'ils ne sont pas en DB
  const displayedRecipients = React.useMemo(() => {
    const active = recipients.filter(r => r.active);
    const existingEmails = new Set(active.map(r => r.email.toLowerCase()));
    const merged = [...active];
    DEFAULT_PRECHECKED_EMAILS.forEach(email => {
      if (!existingEmails.has(email.toLowerCase())) {
        merged.push({ id: `default-${email}`, email, label: '', active: true, is_default: true });
      } else {
        // marque les destinataires DB comme défaut s'ils correspondent
        const idx = merged.findIndex(r => r.email.toLowerCase() === email.toLowerCase());
        if (idx >= 0) merged[idx] = { ...merged[idx], is_default: true };
      }
    });
    return merged;
  }, [recipients]);

  // Calcule la sélection par défaut : tous les destinataires actifs + les emails par défaut
  const buildDefaultSelection = React.useCallback(() => {
    const activeEmails = recipients.filter(r => r.active).map(r => r.email);
    const merged = new Set(activeEmails.map(e => e.toLowerCase()));
    DEFAULT_PRECHECKED_EMAILS.forEach(e => merged.add(e.toLowerCase()));
    return Array.from(merged);
  }, [recipients]);

  const fetchAnnouncements = async () => {
    try {
      const res = await fetch(`${API_URL}/api/announcements`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setAnnouncements(await res.json());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/announcements/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDistributionEmail(data.distribution_email || 'all@wasabicorner.fr');
        setEmailTemplate(data.email_template || '');
      }
    } catch (e) { console.error(e); }
  };

  const fetchRecipients = async () => {
    setLoadingRecipients(true);
    try {
      const res = await fetch(`${API_URL}/api/announcement-recipients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setRecipients(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoadingRecipients(false); }
  };

  const addRecipient = async () => {
    if (!newRecipientEmail.trim() || !newRecipientEmail.includes('@')) {
      toast.error('Adresse email invalide'); return;
    }
    try {
      const res = await fetch(`${API_URL}/api/announcement-recipients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ email: newRecipientEmail, label: newRecipientLabel || '' })
      });
      if (res.ok) {
        toast.success('Destinataire ajouté');
        setNewRecipientEmail('');
        setNewRecipientLabel('');
        fetchRecipients();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch { toast.error('Erreur réseau'); }
  };

  const toggleRecipientActive = async (r) => {
    try {
      await fetch(`${API_URL}/api/announcement-recipients/${r.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ active: !r.active })
      });
      fetchRecipients();
    } catch { toast.error('Erreur'); }
  };

  const deleteRecipient = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/announcement-recipients/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Destinataire supprimé');
        fetchRecipients();
      }
    } catch { toast.error('Erreur'); }
  };

  useEffect(() => {
    if (token) {
      fetchAnnouncements();
      if (isAdmin) {
        fetchSettings();
        fetchRecipients();
      }
    }
  }, [token]);

  const openApproveDialog = (ann) => {
    setSelectedAnn(ann);
    // Pre-select all active recipients + DEFAULT_PRECHECKED_EMAILS
    setSelectedRecipients(buildDefaultSelection());
    setExtraRecipient('');
    setShowApproveDialog(true);
  };

  const addExtraRecipientToSelection = () => {
    if (!extraRecipient.trim() || !extraRecipient.includes('@')) {
      toast.error('Adresse invalide'); return;
    }
    if (selectedRecipients.includes(extraRecipient.trim().toLowerCase())) {
      toast.error('Déjà dans la liste'); return;
    }
    setSelectedRecipients(prev => [...prev, extraRecipient.trim().toLowerCase()]);
    setExtraRecipient('');
  };

  const handleApprove = async (ann) => {
    setActionLoading(ann.id);
    try {
      const res = await fetch(`${API_URL}/api/announcements/${ann.id}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ selected_recipients: selectedRecipients })
      });
      if (res.ok) {
        toast.success(`Annonce validée et envoyée à ${selectedRecipients.length} destinataire(s) !`);
        fetchAnnouncements();
        setShowPreview(false);
        setShowApproveDialog(false);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch { toast.error('Erreur réseau'); }
    finally { setActionLoading(null); }
  };

  const handleReject = async () => {
    if (!selectedAnn) return;
    setActionLoading(selectedAnn.id);
    try {
      const res = await fetch(`${API_URL}/api/announcements/${selectedAnn.id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ comment: rejectComment })
      });
      if (res.ok) {
        toast.success('Annonce refusée');
        fetchAnnouncements();
        setShowRejectDialog(false);
        setShowPreview(false);
        setRejectComment('');
      }
    } catch { toast.error('Erreur réseau'); }
    finally { setActionLoading(null); }
  };

  const handleDelete = async () => {
    if (!annToDelete) return;
    setActionLoading(annToDelete.id);
    try {
      const res = await fetch(`${API_URL}/api/announcements/${annToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Annonce supprimée');
        fetchAnnouncements();
        setShowDeleteDialog(false);
        setAnnToDelete(null);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch { toast.error('Erreur réseau'); }
    finally { setActionLoading(null); }
  };

  const handleSaveEdit = async () => {
    if (!selectedAnn || !editContent) return;
    setActionLoading(selectedAnn.id);
    try {
      const res = await fetch(`${API_URL}/api/announcements/${selectedAnn.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ content: editContent })
      });
      if (res.ok) {
        toast.success('Annonce modifiée');
        fetchAnnouncements();
        setShowEditModal(false);
      }
    } catch { toast.error('Erreur réseau'); }
    finally { setActionLoading(null); }
  };

  const saveSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/announcements/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ distribution_email: distributionEmail, email_template: emailTemplate })
      });
      if (res.ok) {
        toast.success('Paramètres sauvegardés');
        setShowSettings(false);
      }
    } catch { toast.error('Erreur'); }
  };

  const filtered = announcements.filter(a => filter === 'all' || a.status === filter);

  const pendingCount = announcements.filter(a => a.status === 'en_attente').length;

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto space-y-6" data-testid="announcements-page">
        {/* En-tête §6.8 */}
        <PageHeader overline="Réseau · Annonces de signature" title="ANNONCES">
          {pendingCount > 0 && (
            <span className="inline-flex items-center h-7 px-2.5 rounded-full bg-orange text-orange-on text-xs font-bold" data-testid="pending-badge">
              <Clock className="w-3 h-3 mr-1" /> {pendingCount} à traiter
            </span>
          )}
          {isAdmin && (
            <button onClick={() => setShowSettings(true)} data-testid="settings-btn" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-ctrl text-sm font-semibold bg-surface border border-border text-ink hover:bg-surface-2 transition-colors">
              <Settings className="w-4 h-4" /> Paramètres
            </button>
          )}
        </PageHeader>

        {/* Filtres segmentés avec compteurs §6.8 */}
        <div className="overflow-x-auto" data-testid="announcements-filters">
          <Segmented
            value={filter}
            onChange={setFilter}
            options={[
              { value: 'all', label: `Toutes (${announcements.length})` },
              { value: 'en_attente', label: `En attente (${announcements.filter(a => a.status === 'en_attente').length})` },
              { value: 'valide', label: `Validées (${announcements.filter(a => a.status === 'valide').length})` },
              { value: 'refuse', label: `Refusées (${announcements.filter(a => a.status === 'refuse').length})` },
              { value: 'brouillon', label: `Brouillons (${announcements.filter(a => a.status === 'brouillon').length})` },
            ]}
          />
        </div>

        {/* Liste en tableau §6.8 */}
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-orange" /></div>
        ) : filtered.length === 0 ? (
          <div className="rounded-card bg-surface py-10 text-center text-text-3">
            Aucune annonce {filter !== 'all' ? `avec le statut « ${STATUS_CONFIG[filter]?.label} »` : ''} pour le moment.
          </div>
        ) : (
          <div className="rounded-card bg-surface overflow-x-auto" data-testid="announcements-list">
            <table className="w-full text-sm min-w-[880px]">
              <thead>
                <tr className="border-b border-[#E6E4DE]">
                  <th className="text-left font-bold uppercase text-[11px] tracking-[0.1em] text-label px-4 py-3">Magasin</th>
                  <th className="text-left font-bold uppercase text-[11px] tracking-[0.1em] text-label px-4 py-3">Statut</th>
                  <th className="text-left font-bold uppercase text-[11px] tracking-[0.1em] text-label px-4 py-3">Auteur</th>
                  <th className="text-left font-bold uppercase text-[11px] tracking-[0.1em] text-label px-4 py-3">Date</th>
                  <th className="text-left font-bold uppercase text-[11px] tracking-[0.1em] text-label px-4 py-3">Validé / Refusé par</th>
                  <th className="text-right font-bold uppercase text-[11px] tracking-[0.1em] text-label px-4 py-3">Destinataires</th>
                  <th className="text-right font-bold uppercase text-[11px] tracking-[0.1em] text-label px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(ann => {
                  const cfg = STATUS_CONFIG[ann.status] || STATUS_CONFIG.brouillon;
                  const IconComp = cfg.icon;
                  return (
                    <tr key={ann.id} className="border-b border-divider hover:bg-surface-2 align-top" data-testid={`announcement-${ann.id}`}>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-ink">{ann.content?.enseigne} – {ann.content?.ville}</p>
                        {ann.reject_comment && <p className="text-xs text-danger mt-0.5">Motif : {ann.reject_comment}</p>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 ${cfg.color} text-[12px] font-bold px-2.5 py-1 rounded-[6px]`}>
                          <IconComp className="w-3 h-3" /> {cfg.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-text-2">{ann.created_by_name}</td>
                      <td className="px-4 py-3 text-text-2 whitespace-nowrap">{safeDate(ann.created_at)}</td>
                      <td className="px-4 py-3 text-text-2">{ann.validated_by_name || ann.rejected_by_name || '—'}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-text-2">
                        {ann.sent_to && ann.sent_to.length > 0 ? ann.sent_to.length : '—'}
                        {ann.attachments && ann.attachments.length > 0 && <span className="block text-xs text-text-3">{ann.attachments.length} PDF</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            aria-label="Voir"
                            onClick={async () => {
                              setSelectedAnn(ann);
                              setShowPreview(true);
                              setPreviewEditMode(false);
                              setSelectedRecipients(buildDefaultSelection());
                              setExtraRecipient('');
                              try {
                                const res = await fetch(`${API_URL}/api/announcements/${ann.id}/preview-html`, {
                                  headers: { 'Authorization': `Bearer ${token}` }
                                });
                                if (res.ok) {
                                  const data = await res.json();
                                  setPreviewHtml(data.html || '');
                                  setIsCustomHtml(!!data.is_custom);
                                }
                              } catch { setPreviewHtml(''); setIsCustomHtml(false); }
                            }}
                            className="h-9 w-9 inline-flex items-center justify-center rounded-ctrl text-text-2 hover:bg-surface-2"
                            data-testid={`preview-btn-${ann.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {isAdmin && ann.status === 'en_attente' && (
                            <>
                              <button
                                aria-label="Modifier"
                                onClick={() => { setSelectedAnn(ann); setEditContent(ann.content); setShowEditModal(true); }}
                                className="h-9 w-9 inline-flex items-center justify-center rounded-ctrl text-text-2 hover:bg-surface-2"
                                data-testid={`edit-btn-${ann.id}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => openApproveDialog(ann)}
                                disabled={actionLoading === ann.id}
                                className="h-9 px-3 inline-flex items-center gap-1 rounded-ctrl text-sm font-bold bg-orange text-orange-on hover:brightness-105 disabled:opacity-60"
                                data-testid={`approve-btn-${ann.id}`}
                              >
                                {actionLoading === ann.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                                Valider
                              </button>
                              <button
                                onClick={() => { setSelectedAnn(ann); setShowRejectDialog(true); }}
                                className="h-9 px-3 inline-flex items-center gap-1 rounded-ctrl text-sm font-semibold bg-surface border border-border text-danger hover:bg-surface-2"
                                data-testid={`reject-btn-${ann.id}`}
                              >
                                <XCircle className="w-4 h-4" /> Refuser
                              </button>
                            </>
                          )}
                          {isAdmin && (
                            <button
                              aria-label="Supprimer"
                              onClick={() => { setAnnToDelete(ann); setShowDeleteDialog(true); }}
                              className="h-9 w-9 inline-flex items-center justify-center rounded-ctrl text-text-3 hover:text-danger hover:bg-surface-2"
                              data-testid={`delete-btn-${ann.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Preview & Action Dialog */}
        <Dialog open={showPreview} onOpenChange={(open) => { setShowPreview(open); if (!open) { setPreviewHtml(''); setPreviewEditMode(false); setHtmlEditMode(false); setEditedHtml(''); } }}>
          <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="preview-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Megaphone className="w-5 h-5 text-emerald-600" />
                {selectedAnn?.content?.enseigne} – {selectedAnn?.content?.ville}
                <Badge className={selectedAnn?.status === 'en_attente' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}>
                  {selectedAnn?.status === 'en_attente' ? 'En attente' : selectedAnn?.status === 'validee' ? 'Envoyé' : selectedAnn?.status}
                </Badge>
              </DialogTitle>
            </DialogHeader>

            {/* Tabs: Preview / Edit (only for en_attente) */}
            {isAdmin && selectedAnn?.status === 'en_attente' && (
              <div className="flex gap-2 border-b pb-2 flex-wrap">
                <Button
                  variant={!previewEditMode && !htmlEditMode ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => { setPreviewEditMode(false); setHtmlEditMode(false); }}
                  className={!previewEditMode && !htmlEditMode ? 'bg-emerald-600 text-white' : ''}
                  data-testid="tab-preview"
                >
                  <Eye className="w-4 h-4 mr-1" /> Aperçu mail
                </Button>
                <Button
                  variant={previewEditMode ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => { setPreviewEditMode(true); setHtmlEditMode(false); setEditContent(selectedAnn?.content); }}
                  className={previewEditMode ? 'bg-blue-600 text-white' : ''}
                  data-testid="tab-edit-text"
                >
                  <Pencil className="w-4 h-4 mr-1" /> Modifier le texte
                </Button>
                <Button
                  variant={htmlEditMode ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => { setHtmlEditMode(true); setPreviewEditMode(false); setEditedHtml(previewHtml); }}
                  className={htmlEditMode ? 'bg-purple-600 text-white' : ''}
                  data-testid="tab-edit-html"
                >
                  <Pencil className="w-4 h-4 mr-1" /> Modifier l'email complet
                </Button>
              </div>
            )}

            {/* Preview mode */}
            {!previewEditMode && !htmlEditMode && (
              <>
                {isCustomHtml && (
                  <div className="px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg text-xs text-purple-700 flex items-center gap-2">
                    <Pencil className="w-3.5 h-3.5" />
                    <span><strong>Email personnalisé actif</strong> — le contenu ci-dessous a été modifié manuellement et sera envoyé tel quel.</span>
                  </div>
                )}
                {previewHtml ? (
                  <div 
                    className="border rounded-lg overflow-hidden"
                    dangerouslySetInnerHTML={{ __html: previewHtml }}
                    data-testid="preview-html-content"
                  />
                ) : (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
                  </div>
                )}
              </>
            )}

            {/* Edit mode */}
            {previewEditMode && editContent && (
              <div className="space-y-3 border rounded-lg p-4 bg-slate-50">
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Message de clôture</label>
                  <Textarea
                    value={editContent.message_final || ''}
                    onChange={(e) => setEditContent(prev => ({ ...prev, message_final: e.target.value }))}
                    className="min-h-20 text-sm"
                    placeholder="Message de clôture..."
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Particularité CAPEX</label>
                  <Textarea
                    value={editContent.investissement_capex?.particularite || ''}
                    onChange={(e) => setEditContent(prev => ({
                      ...prev,
                      investissement_capex: { ...prev.investissement_capex, particularite: e.target.value }
                    }))}
                    className="min-h-16 text-sm"
                    placeholder="Particularités du CAPEX..."
                  />
                </div>
                <Button
                  size="sm"
                  onClick={async () => {
                    try {
                      const res = await fetch(`${API_URL}/api/announcements/${selectedAnn.id}`, {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                        body: JSON.stringify({ content: editContent })
                      });
                      if (res.ok) {
                        toast.success('Contenu mis à jour');
                        setSelectedAnn(prev => ({ ...prev, content: editContent }));
                        setPreviewEditMode(false);
                        // Refresh preview HTML
                        const r2 = await fetch(`${API_URL}/api/announcements/${selectedAnn.id}/preview-html`, {
                          headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (r2.ok) { const d = await r2.json(); setPreviewHtml(d.html || ''); }
                        fetchAnnouncements();
                      } else { toast.error('Erreur lors de la mise à jour'); }
                    } catch { toast.error('Erreur réseau'); }
                  }}
                  className="bg-blue-600 hover:bg-blue-700 text-white gap-1"
                >
                  <Save className="w-4 h-4" /> Sauvegarder les modifications
                </Button>
              </div>
            )}

            {/* HTML Edit mode */}
            {htmlEditMode && (
              <div className="space-y-3 border rounded-lg p-4 bg-purple-50/30 border-purple-200">
                <div className="flex items-start gap-2 p-2 bg-purple-100 rounded text-xs text-purple-800">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold mb-0.5">Mode expert : édition du HTML complet</p>
                    <p>Vous pouvez modifier directement le code HTML qui sera envoyé. Ce contenu remplacera le rendu automatique. Cliquez sur "Réinitialiser" pour revenir au modèle.</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-600 mb-1 block font-medium">Source HTML</label>
                    <Textarea
                      value={editedHtml}
                      onChange={(e) => setEditedHtml(e.target.value)}
                      className="font-mono text-xs"
                      style={{ minHeight: '40vh' }}
                      data-testid="html-editor-textarea"
                      spellCheck={false}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-600 mb-1 block font-medium">Aperçu en direct</label>
                    <div
                      className="border rounded-lg overflow-auto bg-white"
                      style={{ minHeight: '40vh', maxHeight: '40vh' }}
                      dangerouslySetInnerHTML={{ __html: editedHtml }}
                      data-testid="html-live-preview"
                    />
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    onClick={async () => {
                      setSavingHtml(true);
                      try {
                        const res = await fetch(`${API_URL}/api/announcements/${selectedAnn.id}`, {
                          method: 'PATCH',
                          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                          body: JSON.stringify({ custom_html: editedHtml }),
                        });
                        if (res.ok) {
                          toast.success('Email personnalisé sauvegardé');
                          setPreviewHtml(editedHtml);
                          setIsCustomHtml(true);
                          setHtmlEditMode(false);
                          fetchAnnouncements();
                        } else {
                          toast.error('Erreur lors de la sauvegarde');
                        }
                      } catch { toast.error('Erreur réseau'); }
                      finally { setSavingHtml(false); }
                    }}
                    disabled={savingHtml || !editedHtml.trim()}
                    className="bg-purple-600 hover:bg-purple-700 text-white gap-1"
                    data-testid="save-html-btn"
                  >
                    {savingHtml ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Sauvegarder l'email personnalisé
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      try {
                        const res = await fetch(`${API_URL}/api/announcements/${selectedAnn.id}`, {
                          method: 'PATCH',
                          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                          body: JSON.stringify({ reset_custom_html: true }),
                        });
                        if (res.ok) {
                          toast.success('Email réinitialisé au modèle automatique');
                          setIsCustomHtml(false);
                          // Reload preview from template
                          const r2 = await fetch(`${API_URL}/api/announcements/${selectedAnn.id}/preview-html`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                          });
                          if (r2.ok) {
                            const d = await r2.json();
                            setPreviewHtml(d.html || '');
                            setEditedHtml(d.html || '');
                          }
                          fetchAnnouncements();
                        }
                      } catch { toast.error('Erreur réseau'); }
                    }}
                    disabled={savingHtml}
                    data-testid="reset-html-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-1" /> Réinitialiser au modèle
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => { setHtmlEditMode(false); }}
                    data-testid="cancel-html-edit-btn"
                  >
                    Annuler
                  </Button>
                </div>
              </div>
            )}

            {/* Pièces jointes */}
            {selectedAnn?.attachments?.length > 0 && (
              <div className="border rounded-lg p-4 bg-slate-50 dark:bg-slate-900" data-testid="preview-attachments">
                <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-red-500" />
                  Pièces jointes ({selectedAnn.attachments.length})
                </p>
                <div className="space-y-1.5">
                  {selectedAnn.attachments.map((att) => (
                    <div key={att.filename} className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 bg-white dark:bg-slate-800 rounded px-3 py-2 border">
                      <FileText className="w-3.5 h-3.5 text-red-500 shrink-0" />
                      <span className="flex-1 truncate">{att.original_name}</span>
                      {att.category && (
                        <span className="shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                          {att.category}
                        </span>
                      )}
                      <span className="shrink-0 text-xs text-slate-400">{(att.size / 1024).toFixed(0)} Ko</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {(!selectedAnn?.attachments || selectedAnn?.attachments?.length === 0) && (
              <p className="text-xs text-slate-400 italic text-center py-1">Aucune pièce jointe</p>
            )}

            {/* Recipients + Send (only for en_attente) */}
            {isAdmin && selectedAnn?.status === 'en_attente' && (
              <div className="border-t pt-4 space-y-3">
                <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <Users className="w-4 h-4 text-emerald-600" /> Destinataires
                </h3>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {displayedRecipients.map(r => (
                    <label key={r.id} className="flex items-center gap-3 p-2 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer" data-testid={`preview-recipient-${r.id}`}>
                      <Checkbox
                        checked={selectedRecipients.map(e => e.toLowerCase()).includes(r.email.toLowerCase())}
                        onCheckedChange={(checked) => {
                          if (checked) setSelectedRecipients(prev => [...prev, r.email]);
                          else setSelectedRecipients(prev => prev.filter(e => e.toLowerCase() !== r.email.toLowerCase()));
                        }}
                      />
                      <span className="text-sm font-medium flex-1">{r.email}</span>
                    </label>
                  ))}
                  {/* Extra added recipients */}
                  {selectedRecipients.filter(e => !displayedRecipients.find(r => r.email.toLowerCase() === e.toLowerCase())).map(email => (
                    <div key={email} className="flex items-center gap-3 p-2 rounded-lg border border-emerald-200 bg-emerald-50">
                      <Checkbox checked={true} onCheckedChange={() => setSelectedRecipients(prev => prev.filter(e => e.toLowerCase() !== email.toLowerCase()))} />
                      <span className="text-sm font-medium flex-1">{email}</span>
                      <Badge variant="outline" className="text-xs text-emerald-600">Ponctuel</Badge>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    value={extraRecipient}
                    onChange={e => setExtraRecipient(e.target.value)}
                    placeholder="Ajouter un destinataire..."
                    className="flex-1"
                    data-testid="preview-extra-recipient"
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addExtraRecipientToSelection(); } }}
                  />
                  <Button variant="outline" size="sm" onClick={addExtraRecipientToSelection} className="shrink-0 gap-1" data-testid="preview-add-extra-btn">
                    <Plus className="w-4 h-4" /> Ajouter
                  </Button>
                </div>
                <p className="text-xs text-slate-400">{selectedRecipients.length} destinataire(s) sélectionné(s)</p>

                <div className="flex justify-between pt-2">
                  <Button
                    variant="outline"
                    onClick={() => { setShowRejectDialog(true); setShowPreview(false); }}
                    className="text-red-600 border-red-300 hover:bg-red-50"
                  >
                    <XCircle className="w-4 h-4 mr-1" /> Refuser
                  </Button>
                  <Button
                    onClick={() => handleApprove(selectedAnn)}
                    disabled={actionLoading === selectedAnn?.id || selectedRecipients.length === 0}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1"
                    data-testid="preview-send-btn"
                  >
                    {actionLoading === selectedAnn?.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Envoyer à {selectedRecipients.length} destinataire(s)
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Reject Dialog */}
        <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
          <DialogContent className="max-w-md" data-testid="reject-dialog">
            <DialogHeader>
              <DialogTitle className="text-red-600 flex items-center gap-2">
                <XCircle className="w-5 h-5" /> Refuser l'annonce
              </DialogTitle>
            </DialogHeader>
            <Textarea
              value={rejectComment}
              onChange={(e) => setRejectComment(e.target.value)}
              placeholder="Motif du refus (optionnel)..."
              className="min-h-20"
              data-testid="reject-comment-input"
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRejectDialog(false)}>Annuler</Button>
              <Button onClick={handleReject} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-reject-btn">
                Refuser
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Modal (Admin) */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="edit-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Pencil className="w-5 h-5 text-blue-600" /> Modifier l'annonce
              </DialogTitle>
            </DialogHeader>
            {editContent && <AnnouncementPreview content={editContent} />}
            <p className="text-xs text-slate-500">Note : Pour modifier le contenu, utilisez le bouton "Modifier" depuis la liste pour accéder à l'éditeur complet.</p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditModal(false)}>Annuler</Button>
              <Button onClick={handleSaveEdit} className="bg-blue-600 text-white" data-testid="save-edit-btn">
                <Save className="w-4 h-4 mr-1" /> Sauvegarder
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Settings Dialog */}
        <Dialog open={showSettings} onOpenChange={setShowSettings}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="settings-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" /> Paramètres Annonce ALL
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-6">
              {/* Recipients management */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4 text-emerald-600" /> Destinataires par défaut
                </h3>
                <div className="space-y-2">
                  {loadingRecipients ? (
                    <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-emerald-500" /></div>
                  ) : recipients.length === 0 ? (
                    <p className="text-sm text-slate-400 py-2">Aucun destinataire configuré</p>
                  ) : (
                    recipients.map(r => (
                      <div key={r.id} className={`flex items-center gap-3 p-2 rounded-lg border ${r.active ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-100 opacity-60'}`} data-testid={`recipient-${r.id}`}>
                        <Mail className="w-4 h-4 text-slate-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{r.email}</p>
                          {r.label && r.label !== r.email && <p className="text-xs text-slate-400">{r.label}</p>}
                        </div>
                        <Switch checked={r.active} onCheckedChange={() => toggleRecipientActive(r)} data-testid={`toggle-recipient-${r.id}`} />
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600" onClick={() => deleteRecipient(r.id)} data-testid={`delete-recipient-${r.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))
                  )}
                  <div className="flex gap-2 mt-2">
                    <Input
                      value={newRecipientEmail}
                      onChange={e => setNewRecipientEmail(e.target.value)}
                      placeholder="email@exemple.fr"
                      className="flex-1"
                      data-testid="new-recipient-email"
                    />
                    <Input
                      value={newRecipientLabel}
                      onChange={e => setNewRecipientLabel(e.target.value)}
                      placeholder="Libellé (optionnel)"
                      className="w-40"
                      data-testid="new-recipient-label"
                    />
                    <Button size="sm" onClick={addRecipient} className="bg-emerald-500 hover:bg-emerald-600 text-white gap-1 shrink-0" data-testid="add-recipient-btn">
                      <Plus className="w-4 h-4" /> Ajouter
                    </Button>
                  </div>
                </div>
              </div>

              {/* Email template */}
              <div>
                <Label className="text-sm font-semibold text-slate-700">Template email d'annonce</Label>
                <p className="text-xs text-slate-500 mt-1 mb-2">
                  Variables disponibles : {'{enseigne}'}, {'{ville}'}, {'{creator_name}'}, {'{admin_name}'}
                </p>
                <Textarea
                  value={emailTemplate}
                  onChange={(e) => setEmailTemplate(e.target.value)}
                  placeholder="Template de l'email d'annonce..."
                  className="min-h-40 text-sm font-mono"
                  data-testid="email-template-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSettings(false)}>Fermer</Button>
              <Button onClick={saveSettings} className="bg-emerald-600 text-white" data-testid="save-settings-btn">
                <Save className="w-4 h-4 mr-1" /> Sauvegarder
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Approve Dialog with recipient selection */}
        <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
          <DialogContent className="max-w-lg" data-testid="approve-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-emerald-700">
                <Send className="w-5 h-5" /> Valider et Envoyer
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Sélectionnez les destinataires pour l'annonce <strong>{selectedAnn?.content?.enseigne} – {selectedAnn?.content?.ville}</strong> :
              </p>

              {/* Default recipients */}
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {displayedRecipients.map(r => (
                  <label key={r.id} className="flex items-center gap-3 p-2 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer" data-testid={`approve-recipient-${r.id}`}>
                    <Checkbox
                      checked={selectedRecipients.map(e => e.toLowerCase()).includes(r.email.toLowerCase())}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedRecipients(prev => [...prev, r.email]);
                        } else {
                          setSelectedRecipients(prev => prev.filter(e => e.toLowerCase() !== r.email.toLowerCase()));
                        }
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium">{r.email}</span>
                    </div>
                  </label>
                ))}
                {/* Extra added recipients */}
                {selectedRecipients.filter(e => !displayedRecipients.find(r => r.email.toLowerCase() === e.toLowerCase())).map(email => (
                  <div key={email} className="flex items-center gap-3 p-2 rounded-lg border border-emerald-200 bg-emerald-50">
                    <Checkbox checked={true} onCheckedChange={() => setSelectedRecipients(prev => prev.filter(e => e !== email))} />
                    <span className="text-sm font-medium flex-1">{email}</span>
                    <Badge variant="outline" className="text-xs text-emerald-600">Ponctuel</Badge>
                  </div>
                ))}
              </div>

              {/* Add extra recipient */}
              <div className="flex gap-2">
                <Input
                  value={extraRecipient}
                  onChange={e => setExtraRecipient(e.target.value)}
                  placeholder="Ajouter un destinataire ponctuel..."
                  className="flex-1"
                  data-testid="extra-recipient-input"
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addExtraRecipientToSelection(); } }}
                />
                <Button variant="outline" size="sm" onClick={addExtraRecipientToSelection} className="shrink-0 gap-1" data-testid="add-extra-recipient-btn">
                  <Plus className="w-4 h-4" /> Ajouter
                </Button>
              </div>

              <p className="text-xs text-slate-400">
                {selectedRecipients.length} destinataire(s) sélectionné(s)
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowApproveDialog(false)}>Annuler</Button>
              <Button
                onClick={() => handleApprove(selectedAnn)}
                disabled={actionLoading === selectedAnn?.id || selectedRecipients.length === 0}
                className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1"
                data-testid="confirm-approve-btn"
              >
                {actionLoading === selectedAnn?.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Envoyer à {selectedRecipients.length} destinataire(s)
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="max-w-md" data-testid="delete-dialog">
            <DialogHeader>
              <DialogTitle className="text-red-600 flex items-center gap-2">
                <Trash2 className="w-5 h-5" /> Supprimer l'annonce
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-600">
              Voulez-vous vraiment supprimer l'annonce <strong>{annToDelete?.content?.enseigne} – {annToDelete?.content?.ville}</strong> ?
              Cette action est irréversible.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowDeleteDialog(false); setAnnToDelete(null); }}>Annuler</Button>
              <Button
                onClick={handleDelete}
                disabled={actionLoading === annToDelete?.id}
                className="bg-red-600 hover:bg-red-700 text-white gap-1"
                data-testid="confirm-delete-btn"
              >
                {actionLoading === annToDelete?.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Supprimer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
